import { useCallback, useEffect, useState } from "react";
import { Search, Users } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { OpsShell } from "@/components/admin/OpsShell";
import { Customer360 } from "@/components/admin/Customer360";
import { Btn, Card, Empty, fmtDay, Loading, Pager, Pill, T } from "@/components/admin/ui";

const FILTERS = [
  ["all", "All customers"],
  ["active", "Active"],
  ["trialing", "Trialing"],
  ["expired", "Expired"],
  ["cancelled", "Cancelled"],
  ["lifetime", "Lifetime"],
  ["disabled", "Disabled"],
];

export default function AdminUsers() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(page), page_size: "25" });
      if (q.trim()) params.set("q", q.trim());
      if (status !== "all") params.set("status", status);
      const { data } = await api.get(`/admin/users?${params}`);
      setD(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [q, status, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <OpsShell title="Customers" subtitle="Search any workspace, then adjust plan, credits or validity">
      <Card
        testid="admin-users-panel"
        title="Directory"
        hint={d ? `${d.total} customers` : ""}
        actions={
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                data-testid="admin-users-search"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(1);
                }}
                placeholder="Search email or name"
                className={`${T.input} pl-8 w-56 py-1.5`}
              />
            </div>
            <select
              data-testid="admin-users-filter"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
              className={`${T.input} w-36 py-1.5`}
            >
              {FILTERS.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </div>
        }
      >
        {!d ? (
          <Loading label="Loading customers" />
        ) : d.items.length === 0 ? (
          <Empty label="No customers match this filter" hint="Try a different status or clear the search" icon={Users} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse min-w-[940px]">
                <thead>
                  <tr>
                    <th className={T.th}>Customer</th>
                    <th className={T.th}>Workspace</th>
                    <th className={T.th}>Plan</th>
                    <th className={T.th}>Status</th>
                    <th className={T.th}>Credits</th>
                    <th className={T.th}>Renews / expires</th>
                    <th className={T.th}>Joined</th>
                    <th className={T.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {d.items.map((u) => (
                    <tr key={u.id} className={T.row} data-testid={`admin-user-row-${u.id}`}>
                      <td className={T.td}>
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-800 grid place-items-center text-[11px] font-semibold text-slate-600 dark:text-slate-300 shrink-0">
                            {(u.name || u.email || "?").slice(0, 2).toUpperCase()}
                          </span>
                          <div className="min-w-0">
                            <p className="text-[13px] font-medium text-slate-800 dark:text-slate-200 truncate">{u.name || "—"}</p>
                            <p className={`text-xs truncate ${T.muted}`}>{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className={T.td}>
                        <p className="text-[13px] truncate max-w-[150px]">{u.org_name || "—"}</p>
                        <p className={`text-[11px] uppercase tracking-wide ${T.muted}`}>{u.workspace_role}</p>
                      </td>
                      <td className={T.td}>
                        {u.lifetime ? <Pill value="lifetime" /> : <span className="text-[13px]">{u.plan_label || "—"}</span>}
                      </td>
                      <td className={T.td}>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Pill value={u.disabled ? "disabled" : u.sub_status} />
                          {u.platform_role && (
                            <span className="rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide">
                              staff
                            </span>
                          )}
                        </div>
                      </td>
                      <td className={`${T.td} ${T.num}`}>
                        {u.proposals_left ?? 0}
                        <span className={T.muted}>/{u.proposal_limit ?? 0}</span>
                      </td>
                      <td className={`${T.td} ${T.num} ${T.muted}`}>{u.lifetime ? "never" : fmtDay(u.expiry)}</td>
                      <td className={`${T.td} ${T.num} ${T.muted}`}>{fmtDay(u.signup_date)}</td>
                      <td className={T.td}>
                        <Btn data-testid={`admin-manage-user-${u.id}`} onClick={() => setOpen(u.id)}>
                          Manage
                        </Btn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager page={d.page} pages={d.pages} total={d.total} onPage={setPage} testid="admin-users-pager" />
          </>
        )}
      </Card>

      {open && <Customer360 userId={open} onClose={() => setOpen(null)} onChanged={load} />}
    </OpsShell>
  );
}
